<template>
  <v-row>
    <v-breadcrumbs :items="crumbs" divider="/"></v-breadcrumbs>
  </v-row>
  <v-row>
    <v-col cols="12" md="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">position</v-card-title>
          <template v-slot:append>
            <v-tooltip text="toggle base currency" location="top">
              <template v-slot:activator="{ props }"><v-btn v-if="this.currency != 'AUD'" v-bind="props" icon="mdi-currency-usd" :variant="isBaseCurrency ? 'tonal' : 'text'" @click="this.isBaseCurrency = !this.isBaseCurrency"></v-btn></template>
            </v-tooltip>
            <v-tooltip text="manual price update" location="top">
              <template v-slot:activator="{ props }"><v-btn v-bind="props" icon="mdi-cash-edit" @click="showModal = true" variant="text"></v-btn></template>
            </v-tooltip>
            <v-tooltip text="refresh prices" location="top">
              <template v-slot:activator="{ props }"><v-btn v-bind="props" icon="mdi-refresh" @click="refreshSecurityPrices()" variant="text"></v-btn></template>
            </v-tooltip>
          </template>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text>
          <v-container>          
            <v-row>
              <v-col cols="12" md="3" >
                <p class="font-weight-light">units</p>
                <p class="text-h4">{{ Number(position.units).toLocaleString() }}</p>
              </v-col>
              <v-col cols="12" md="3">
                <p class="font-weight-light">price</p>
                <Transition mode="out-in" name="fade-in">
                  <p class="text-h4" :key="isBaseCurrency">{{ (isBaseCurrency ? position.base_currency.price : position.price) }}</p>
                </Transition>
              </v-col>
              <v-col cols="12" md="3">
                <p class="font-weight-light">value</p>
                <Transition mode="out-in" name="fade-in">
                  <p class="text-h4" :key="isBaseCurrency">{{ Number(isBaseCurrency ? position.base_currency.market_value : position.market_value).toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) }}</p>
                </Transition>
                </v-col>
              <v-col cols="12" md="3">
                <p class="font-weight-light">yield %</p>
                <p class="text-h4">{{ position.yield_pct }}</p>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="3">
                <p class="font-weight-light">running unit cost</p>
                <Transition mode="out-in" name="fade-in">
                  <p class="text-h4" :key="isBaseCurrency">{{ (isBaseCurrency ? position.base_currency.running_unit_cost : position.running_unit_cost) }}</p>
                </Transition>
              </v-col>
              <v-col cols="12" md="3">
                <p class="font-weight-light">running cost base</p>
                <Transition mode="out-in" name="fade-in">
                  <p class="text-h4" :key="isBaseCurrency">{{ Number(isBaseCurrency ? position.base_currency.running_cost_base : position.running_cost_base).toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) }}</p>
                </Transition>
              </v-col>
              <v-col cols="12" md="3">
                <p class="font-weight-light">profit</p>
                <Transition mode="out-in" name="fade-in">
                  <p class="text-h4" :class="getTextColour(isBaseCurrency ? position.base_currency.profit?.open_position_total : position.profit?.open_position_total)" :key="isBaseCurrency">{{ Number(isBaseCurrency ? position.base_currency.profit?.open_position_total : position.profit?.open_position_total).toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 0}) }}</p>
                </Transition>
              </v-col>
              <v-col cols="12" md="3">
                <p class="font-weight-light">irr %</p>
                <Transition mode="out-in" name="fade-in">
                  <p class="text-h4" :class="getTextColour(isBaseCurrency ? position.base_currency.irr?.open_position : position.irr?.open_position)" :key="isBaseCurrency">{{ (isBaseCurrency ? position.base_currency.irr?.open_position : position.irr?.open_position) != null ? (isBaseCurrency ? position.base_currency.irr?.open_position : position.irr?.open_position) : '--' }}</p>
                </Transition>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <v-row>
    <v-col cols="12" md="12">
      <v-card :elevation="1">
        <div class="d-flex align-center py-3">
          <v-tabs v-model="activeTab">
            <v-tab value="txns">txns</v-tab>
            <v-tab value="distributions">distributions</v-tab>
          </v-tabs>
          <v-spacer></v-spacer>
          <template v-if="activeTab === 'txns'">
            <v-btn v-if="this.currency != 'AUD'" icon="mdi-currency-usd" :variant="isBaseCurrency ? 'tonal' : 'text'" @click="this.isBaseCurrency = !this.isBaseCurrency"></v-btn>
          </template>
          <template v-if="activeTab === 'distributions'">
            <v-tooltip text="fetch from yahoo" location="top">
              <template v-slot:activator="{ props }"><v-btn v-bind="props" icon="mdi-download-outline" variant="text" @click="showFetchDialog = true"></v-btn></template>
            </v-tooltip>
            <v-tooltip text="add distribution manually" location="top">
              <template v-slot:activator="{ props }"><v-btn v-bind="props" icon="mdi-plus" variant="text" @click="showDividendModal = true"></v-btn></template>
            </v-tooltip>
          </template>
        </div>
        <v-divider></v-divider>

        <!-- fetch date range dialog -->
        <v-dialog v-model="showFetchDialog" width="420">
          <v-card>
            <v-card-title class="text-h6 text-medium-emphasis ps-4 pt-4">fetch from yahoo finance</v-card-title>
            <v-card-text>
              <v-row>
                <v-col>
                  <v-text-field v-model="fetchStartDate" type="date" label="start date" density="compact" variant="outlined" hide-details></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field v-model="fetchEndDate" type="date" label="end date" density="compact" variant="outlined" hide-details></v-text-field>
                </v-col>
              </v-row>
              <p class="text-caption text-grey mt-3">note: if you participate in a DRP (dividend reinvestment plan), delete the fetched entries for this security to avoid double-counting income in the performance report.</p>
            </v-card-text>
            <v-card-actions class="justify-end pa-4">
              <v-btn variant="text" @click="showFetchDialog = false">cancel</v-btn>
              <v-btn color="primary" :loading="fetchLoading" :disabled="!fetchStartDate || !fetchEndDate" @click="fetchDividendsFromYahoo">fetch</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <div ref="mainWindow" :style="windowMinHeight ? { minHeight: windowMinHeight + 'px' } : {}">
          <Transition name="tab-fade" mode="out-in">
            <div v-if="activeTab === 'txns'" key="txns">
              <v-card-text>
                <v-container class="table-scroll">
                  <table>
                    <tr>
                      <th>txn date</th>
                      <th>ticker</th>
                      <th>currency</th>
                      <th>type</th>
                      <th>qty</th>
                      <th>price</th>
                      <th>gross amount</th>
                      <th>brokerage</th>
                      <th>net amount</th>
                      <th v-if="this.currency != 'AUD'">spot fx</th>
                      <th>running unit cost</th>
                      <th>running cost base</th>
                      <th></th>
                    </tr>
                    <tr v-for="txn in broker_txns.slice().reverse()" :key="txn.id">
                      <td>{{ showDateFormat(txn.txn_date) }}</td>
                      <td>{{ txn.ticker }}</td>
                      <Transition mode="out-in" name="fade-in">
                        <td :key="isBaseCurrency">{{ isBaseCurrency ? txn.base_currency.currency : txn.currency }}</td>
                      </Transition>
                      <td>{{ formatTxnType(txn.txn_type) }}</td>
                      <td>{{ txn.qty.toLocaleString() }}</td>
                      <Transition mode="out-in" name="fade-in">
                        <td :key="isBaseCurrency">{{ (isBaseCurrency ? txn.base_currency.price : txn.price).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
                      </Transition>
                      <Transition mode="out-in" name="fade-in">
                        <td :key="isBaseCurrency">{{ (isBaseCurrency ? txn.base_currency.gross_amount : txn.gross_amount).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
                      </Transition>
                      <Transition mode="out-in" name="fade-in">
                        <td :key="isBaseCurrency">{{ (isBaseCurrency ? txn.base_currency.brokerage : txn.brokerage).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
                      </Transition>
                      <Transition mode="out-in" name="fade-in">
                        <td :key="isBaseCurrency">{{ (isBaseCurrency ? txn.base_currency.net_amount : txn.net_amount).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
                      </Transition>
                      <td v-if="this.currency != 'AUD'">{{ txn.base_currency_spot_rate }}</td>
                      <Transition mode="out-in" name="fade-in">
                        <td :key="isBaseCurrency">{{ (isBaseCurrency ? txn.enrichment.running_unit_cost_in_base_currency : txn.enrichment.running_unit_cost).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
                      </Transition>
                      <Transition mode="out-in" name="fade-in">
                        <td :key="isBaseCurrency">{{ (isBaseCurrency ? txn.enrichment.running_cost_base_in_base_currency : txn.enrichment.running_cost_base).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
                      </Transition>
                      <td><v-tooltip v-if="txn.description" location="top"><template v-slot:activator="{ props }"><v-icon v-bind="props" size="x-small" class="description_icon" @click.stop>mdi-note-text-outline</v-icon></template>{{ txn.description }}</v-tooltip></td>
                    </tr>
                  </table>
                </v-container>
              </v-card-text>
            </div>

            <div v-else key="distributions">
              <v-card-text>
                <v-container class="table-scroll">
                  <table>
                    <tr>
                      <th>ex date</th>
                      <th>payment date</th>
                      <th>amount / unit</th>
                      <th>currency</th>
                      <th>franking cr / unit</th>
                      <th>source</th>
                    </tr>
                    <tr v-for="div in dividends" :key="div.id" class="clickable-row" @click="editingDividend = div; showDividendModal = true">
                      <td>{{ showDateFormat(div.ex_date) }}</td>
                      <td>{{ div.payment_date ? showDateFormat(div.payment_date) : '—' }}</td>
                      <td>{{ Number(div.amount_per_unit).toFixed(4) }}</td>
                      <td>{{ div.currency }}</td>
                      <td>{{ div.franking_credits_per_unit != null ? Number(div.franking_credits_per_unit).toFixed(4) : '—' }}</td>
                      <td><span :class="div.source === 'manual' ? 'source-manual' : 'source-yahoo'">{{ div.source }}</span></td>
                    </tr>
                    <tr v-if="dividends.length === 0">
                      <td colspan="6" class="text-grey text-caption pa-4 text-center">no distribution data — use fetch from yahoo or add manually</td>
                    </tr>
                  </table>
                </v-container>
              </v-card-text>
            </div>
          </Transition>
        </div>
      </v-card>
    </v-col>
  </v-row>

  <!-- txn modals -->
  <SecurityPriceManualUpdatePopup :show="showModal" @close="showModal = false" :ticker="this.$route.params.ticker"></SecurityPriceManualUpdatePopup>
  <DividendManualEntryPopup :show="showDividendModal" :ticker="this.$route.params.ticker" :defaultCurrency="currency" :editDividend="editingDividend" @close="showDividendModal = false; editingDividend = null" @saved="loadDividends" @deleted="loadDividends"></DividendManualEntryPopup>

</template>

<script>
import axios from 'axios';
import { mapState, mapMutations} from 'vuex';
import moment from 'moment';
import SecurityPriceManualUpdatePopup from './modals/SecurityPriceManualUpdatePopup.vue';
import DividendManualEntryPopup from './modals/DividendManualEntryPopup.vue';

export default {
  components: {
    SecurityPriceManualUpdatePopup,
    DividendManualEntryPopup,
  },
  data() {
    return {
      response: {},
      position: {},
      currency: "",
      broker_txns: [],
      dividends: [],

      editingDividend: null,
      activeTab: 'txns',
      showModal: false,
      showDividendModal: false,
      showFetchDialog: false,
      isBaseCurrency: false,

      fetchStartDate: '',
      fetchEndDate: new Date().toISOString().slice(0, 10),
      fetchLoading: false,
      windowMinHeight: 0,
    };
  },
  async created() {
    await this.getPositionData();
    await this.loadDividends();
  },
  watch: {
    broker_txns(newVal) {
      if (newVal.length && !this.windowMinHeight) {
        this.$nextTick(() => {
          const el = this.$refs.mainWindow?.$el;
          if (el) this.windowMinHeight = el.offsetHeight;
        });
      }
    },
  },
  computed: {
    ...mapState({
      token: "token"
    }),
    crumbs() {
      return [
        {
          title: 'securities summary',
          disabled: false,
          href: '/securitiessummary'
        },
        {
          title: this.response.account_name,
          disabled: false,
          href: '/brokeraccounts/' + this.$route.params.brokerid
        },
        {
          title: this.$route.params.ticker,
          disabled: true,
          href: ''
        },

      ];
    },

  },
  methods: {
      ...mapMutations(["showSnackbar"]),
    async getPositionData(){
      try {
      const response  = await axios.get("/securities/portfolios/" + this.$route.params.brokerid + "/positions/" + this.$route.params.ticker,
      {
        headers: {
          Authorization: `Bearer ${this.token}`
        }
      });
        this.response = response.data;
        this.position  = this.response.open_positions[0];
        this.currency = this.position.currency;          
        this.broker_txns = this.position.txns;
      
      } catch (error) {
        this.showSnackbar({ message: 'Failed to load position data' });
      }
    },
    showDateFormat(rawDate){
      return moment(rawDate).format('DD/MM/YYYY');
    },
    async refreshSecurityPrices(){
      try {
        const { data } = await axios.request({
          url: "/securities/prices/"+this.$route.params.brokerid,
          method: "POST",
          headers: {
              Authorization: `Bearer ${this.token}`
          },
          data: {
          }
        });
          this.getPositionData(); // refresh local values
      } catch (error) {
        this.showSnackbar({ message: 'Failed to refresh security prices' });
      }
    },
    getTextColour(value){
      if(value < 0){
        return 'red_text';
      }
      else if(value > 0){
        return 'green_text';
      }
    },
    formatTxnType(txn_type){
      const labels = { capital_return_adj: 'capital return adj', tax_adj: 'tax adj' };
      return labels[txn_type] || txn_type;
    },
    async loadDividends() {
      try {
        const { data } = await axios.get('/securities/dividends/' + this.$route.params.ticker, {
          headers: { Authorization: `Bearer ${this.token}` }
        });
        this.dividends = data;
      } catch {
        // non-critical — dividends section stays empty
      }
    },
    async fetchDividendsFromYahoo() {
      this.fetchLoading = true;
      try {
        await axios.post('/securities/dividends/' + this.$route.params.ticker + '/fetch', {
          start_date: this.fetchStartDate,
          end_date: this.fetchEndDate,
        }, { headers: { Authorization: `Bearer ${this.token}` } });
        await this.loadDividends();
        this.showFetchDialog = false;
        this.showSnackbar({ message: 'Dividends fetched from Yahoo Finance', color: 'success' });
      } catch (error) {
        this.showSnackbar({ message: 'Failed to fetch dividends' });
      }
      this.fetchLoading = false;
    },
  }
};

</script>


<style scoped>
.text-h4 {
  font-family: 'Courier New', Courier, monospace, sans-serif !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

table{
  width: 100%;
}

table tr{
  cursor: pointer;
}

.clickable-row {
  cursor: pointer;
}

.source-manual {
  color: #1565C0;
  font-size: 0.75rem;
}

.source-yahoo {
  color: #9E9E9E;
  font-size: 0.75rem;
}


:deep(.v-tab) {
  text-transform: none;
  font-size: 1.25rem;
  font-weight: 500;
  letter-spacing: 0.009375em;
}

.tab-fade-enter-active,
.tab-fade-leave-active {
  transition: opacity 0.08s ease;
}

.tab-fade-enter-from,
.tab-fade-leave-to {
  opacity: 0;
}


.fade-in-enter-active {
  transition: opacity 300ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.fade-in-leave-active {
  transition: opacity 225ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fade-in-enter,
.fade-in-leave-to {
  opacity: 0;
}

.green_text{
  color: green;
}

.red_text{
  color: red;
}

.description_icon{
  opacity: 0.5;
  cursor: pointer;
}

</style>